/**
 * =============================================================================
 * Componente: NewReferralModal
 * Versão: 2.1.0
 *
 * Mudanças v2.1.0 (cirúrgicas):
 *   - Correção: o Select de campanha usava o slug do link pessoal do afiliado
 *     (criado de forma lazy, podendo ser null), o que impedia a seleção.
 *     Agora o valor é o `campaign_id` e o slug é resolvido no submit via
 *     `useEnsureCampaignLink` quando ainda não existir.
 *
 * Mudanças v2.0.0 (cirúrgicas):
 *   - Padronização visual completa espelhando o `TaskFormDialog` (Nova Tarefa):
 *     header premium com ícone em box + ScrollArea + seções com h3+ícone +
 *     labels uppercase compactas + footer padronizado.
 *   - Telefone agora usa `PhoneInput` (com seletor de DDI, máscara BR, suporte
 *     a 0800/4003/400X/300X e truncamento seguro de dígitos).
 *   - Validação de telefone unificada via `validateAffiliatePhone` (mesma regra
 *     da landing pública v1.3.0).
 *   - E-mail sanitizado em tempo real (anti-XSS leve, sem espaços, lowercase) e
 *     validado com `validateEmail` do projeto.
 *   - Nome/Observação sanitizados (remove `< >`); Observação bloqueia URLs.
 *   - Submit-guard anti-duplo-clique (debounce 1s + lock por useRef).
 *   - Payload de telefone normalizado com `normalizeAffiliatePhoneForSubmit`.
 *
 * Descrição:
 *   Modal para o afiliado cadastrar uma indicação manualmente (sem precisar
 *   compartilhar o link). Permite escolher programa + campanha (do próprio
 *   afiliado), preencher dados do lead e submeter via
 *   `useCreateManualAffiliateReferral` (que reaproveita `capture_affiliate_lead`).
 *
 *   Regras de UX:
 *     - Se o afiliado tem 1 programa ativo, ele já vem pré-selecionado.
 *     - Campanha é obrigatória; se houver só 1 do programa, vem pré-selecionada.
 *     - Exige ao menos um entre nome/e-mail/telefone (validação local + backend).
 *     - Telefone com seletor de DDI; backend re-normaliza.
 *     - Fullscreen no mobile (padrão do projeto).
 * =============================================================================
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2,
  UserPlus,
  Layers,
  GitBranch,
  User,
  Phone,
  Mail,
  MessageSquare,
  Megaphone,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PhoneInput } from '@/components/ui/phone-input';
import { useAffiliateMyMemberships } from '@/hooks/useAffiliateMyMemberships';
import {
  useAffiliateMyCampaigns,
  useEnsureCampaignLink,
} from '@/hooks/useAffiliateMyCampaigns';
import { useCreateManualAffiliateReferral } from '@/hooks/useCreateManualAffiliateReferral';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import {
  useAffiliateBranding,
  buildBrandingCssVars,
} from '@/hooks/useAffiliateBranding';
import {
  validateAffiliatePhone,
  normalizeAffiliatePhoneForSubmit,
} from '@/lib/affiliate-phone-validation';
import { validateEmail } from '@/lib/input-validators';
import { cn } from '@/lib/utils';

interface NewReferralModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Sanitiza string removendo `<` `>` (anti-XSS leve antes do envio). */
function sanitizeText(value: string): string {
  return value.replace(/[<>]/g, '');
}

/** Sanitização específica de e-mail: sem `<>`, sem espaços, lowercase. */
function sanitizeEmail(value: string): string {
  return sanitizeText(value).replace(/\s+/g, '').toLowerCase();
}

/** Regex anti-spam: detecta URLs em campos de texto livre. */
const URL_REGEX = /(https?:\/\/|www\.)\S+/i;

type FormState = {
  program_id: string;
  /**
   * v2.1.0 — passamos a guardar o ID da campanha (sempre presente) em vez do
   * slug do link pessoal, que é criado de forma *lazy* e pode ser null.
   */
  campaign_id: string;
  lead_name: string;
  lead_email: string;
  lead_phone: string;
  lead_message: string;
};

const EMPTY: FormState = {
  program_id: '',
  campaign_id: '',
  lead_name: '',
  lead_email: '',
  lead_phone: '',
  lead_message: '',
};

export function NewReferralModal({ open, onOpenChange }: NewReferralModalProps) {
  const { data: memberships, isLoading: loadingMemberships } = useAffiliateMyMemberships();
  const { data: campaigns, isLoading: loadingCampaigns } = useAffiliateMyCampaigns();
  const createMutation = useCreateManualAffiliateReferral();
  // Garante o link pessoal sob demanda quando a campanha ainda não tem slug.
  const ensureLinkMutation = useEnsureCampaignLink();


  // Branding personalizado: o Dialog do Radix renderiza num Portal fora do
  // `.affiliate-shell` da AffiliateLayout — reaplicamos as CSS vars aqui.
  const { affiliate } = useAffiliateAuth();
  const { data: brandingData } = useAffiliateBranding(affiliate?.tenant_id ?? null);
  const brandingStyle = useMemo(
    () => buildBrandingCssVars(brandingData?.branding),
    [brandingData],
  );

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Lock anti-duplo-submit: timestamp do último envio aceito. */
  const lastSubmitAtRef = useRef<number>(0);

  // Programas ativos do afiliado
  const activePrograms = useMemo(
    () => (memberships ?? []).filter((m) => m.status === 'active'),
    [memberships],
  );

  // Campanhas filtradas pelo programa selecionado
  const programCampaigns = useMemo(
    () => (campaigns ?? []).filter((c) => c.program_id === form.program_id),
    [campaigns, form.program_id],
  );

  // ---------------------------------------------------------------------------
  // v2.2.0 — Configuração dos campos exibidos (por campanha).
  // O admin define no cadastro da campanha se E-mail e Observação aparecem.
  // Campanhas legadas (sem config) continuam exibindo todos os campos.
  // ---------------------------------------------------------------------------
  const selectedCampaign = useMemo(
    () => programCampaigns.find((c) => c.campaign_id === form.campaign_id) ?? null,
    [programCampaigns, form.campaign_id],
  );
  const showEmail = selectedCampaign?.referral_form_show_email ?? true;
  const showMessage = selectedCampaign?.referral_form_show_message ?? true;

  // Ao trocar de campanha, limpa os valores dos campos que ficaram ocultos
  // para nunca enviar dado invisível ao afiliado.
  useEffect(() => {
    if (!open) return;
    setForm((prev) => {
      if ((showEmail || !prev.lead_email) && (showMessage || !prev.lead_message)) {
        return prev;
      }
      return {
        ...prev,
        lead_email: showEmail ? prev.lead_email : '',
        lead_message: showMessage ? prev.lead_message : '',
      };
    });
  }, [open, showEmail, showMessage]);

  // Pré-seleção automática quando há apenas 1 programa
  useEffect(() => {
    if (!open) return;
    if (form.program_id) return;
    if (activePrograms.length === 1) {
      setForm((prev) => ({ ...prev, program_id: activePrograms[0].program_id }));
    }
  }, [open, activePrograms, form.program_id]);

  // Pré-seleção automática quando há apenas 1 campanha no programa
  useEffect(() => {
    if (!open || !form.program_id) return;
    if (form.campaign_id) return;
    if (programCampaigns.length === 1) {
      setForm((prev) => ({ ...prev, campaign_id: programCampaigns[0].campaign_id }));
    }
  }, [open, form.program_id, form.campaign_id, programCampaigns]);

  // Reset ao fechar
  useEffect(() => {
    if (!open) {
      setForm(EMPTY);
      setErrors({});
      lastSubmitAtRef.current = 0;
    }
  }, [open]);

  /** Atualiza um campo do formulário e limpa o erro pertinente. */
  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => {
      const next = { ...prev, [key]: value } as FormState;
      // Trocar de programa reseta a campanha
      if (key === 'program_id') next.campaign_id = '';
      return next;
    });
    if (errors[key as string]) {
      setErrors((prev) => {
        const cp = { ...prev };
        delete cp[key as string];
        return cp;
      });
    }
  }

  /** Validação completa do formulário (mesmo padrão da landing). */
  function validateAll(): Record<string, string> {
    const errs: Record<string, string> = {};

    if (!form.program_id) errs.program_id = 'Selecione um programa';
    if (!form.campaign_id) errs.campaign_id = 'Selecione uma campanha';

    // Pelo menos um entre nome/e-mail/telefone (apenas campos visíveis).
    const hasAny = !!(
      form.lead_name.trim() ||
      (showEmail && form.lead_email.trim()) ||
      form.lead_phone.trim()
    );
    if (!hasAny) {
      errs.lead_name = showEmail
        ? 'Informe ao menos nome, e-mail ou telefone'
        : 'Informe ao menos nome ou telefone';
    }

    // Nome: limite e sanitização (já aplicada no onChange).
    if (form.lead_name && form.lead_name.length > 200) {
      errs.lead_name = 'Máx. 200 caracteres';
    }

    // E-mail: validação completa (apenas se visível e preenchido).
    if (showEmail && form.lead_email.trim()) {
      const r = validateEmail(form.lead_email);
      if (!r.isValid) errs.lead_email = r.error || 'E-mail inválido';
      if (form.lead_email.length > 200) errs.lead_email = 'Máx. 200 caracteres';
    }

    // Telefone: usa o validador unificado (apenas se preenchido).
    if (form.lead_phone.trim()) {
      const r = validateAffiliatePhone(form.lead_phone);
      if (!r.isValid) errs.lead_phone = r.error || 'Telefone inválido';
    }

    // Observação: limite + bloqueio de URLs (anti-spam), só se visível.
    if (showMessage && form.lead_message) {
      if (form.lead_message.length > 2000) {
        errs.lead_message = 'Máx. 2000 caracteres';
      } else if (URL_REGEX.test(form.lead_message)) {
        errs.lead_message = 'Links não são permitidos neste campo';
      }
    }


    return errs;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Lock anti-duplo-submit (debounce de 1s).
    const now = Date.now();
    if (
      createMutation.isPending ||
      ensureLinkMutation.isPending ||
      now - lastSubmitAtRef.current < 1000
    ) {
      return;
    }

    const validation = validateAll();
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setErrors({});
    lastSubmitAtRef.current = now;

    // Resolve o slug do link pessoal do afiliado para a campanha escolhida.
    // Se o link ainda não existe (criação lazy), cria sob demanda.
    const selected = programCampaigns.find((c) => c.campaign_id === form.campaign_id);
    if (!selected) {
      setErrors({ campaign_id: 'Campanha inválida. Recarregue a página.' });
      return;
    }

    let slug = selected.slug;
    if (!slug) {
      try {
        const link = await ensureLinkMutation.mutateAsync({
          campaignId: selected.campaign_id,
        });
        slug = link.slug;
      } catch {
        setErrors({ campaign_id: 'Não foi possível gerar o link desta campanha.' });
        return;
      }
    }

    createMutation.mutate(
      {
        slug,
        lead_name: form.lead_name.trim() || null,
        // Campos ocultos pela campanha nunca são enviados.
        lead_email:
          showEmail && form.lead_email.trim()
            ? form.lead_email.trim().toLowerCase()
            : null,
        // Normalização de telefone: defesa em profundidade (backend re-normaliza).
        lead_phone: form.lead_phone.trim()
          ? normalizeAffiliatePhoneForSubmit(form.lead_phone)
          : null,
        lead_message: (showMessage && form.lead_message.trim()) || null,
      },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  }

  const isLoading = loadingMemberships || loadingCampaigns;
  const noActivePrograms = !isLoading && activePrograms.length === 0;
  const noCampaignsForProgram =
    !isLoading && !!form.program_id && programCampaigns.length === 0;
  const isPending = createMutation.isPending || ensureLinkMutation.isPending;


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Shell padronizado com TaskFormDialog: max-w-5xl + fullscreen no mobile.
          Wrapper `.affiliate-shell` interno aplica o branding do tenant
          (cores/gradiente) já que o Dialog é renderizado num Portal. */}
      <DialogContent
        className={cn(
          'affiliate-shell max-w-5xl p-0 gap-0 flex flex-col overflow-hidden',
          'h-[100dvh] max-h-[100dvh] rounded-none',
          'sm:h-auto sm:max-h-[90vh] sm:rounded-lg',
        )}
        style={brandingStyle}
      >
        {/* Header premium — ícone em box + título + descrição contextual */}
        <DialogHeader className="flex flex-row items-center gap-3 px-6 py-4 border-b border-border/50 bg-background/80 backdrop-blur-sm">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 ring-1 ring-primary/20 shrink-0">
            <UserPlus className="w-5 h-5 text-primary" />
          </div>
          <div className="flex flex-col text-left">
            <DialogTitle className="text-base font-semibold leading-tight">
              Nova Indicação
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-0.5">
              Preencha os campos para cadastrar uma nova indicação manualmente
            </DialogDescription>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          {/* Corpo scrollável padronizado com ScrollArea. */}
          <ScrollArea className="flex-1 overflow-auto">
            <div className="p-6 space-y-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : noActivePrograms ? (
                <div className="rounded-md border border-dashed border-border/60 bg-muted/30 p-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Você ainda não tem nenhum programa ativo para indicar.
                  </p>
                </div>
              ) : (
                <>
                  {/* ═══════════════════════════════════════════ */}
                  {/* Seção: Indicação                            */}
                  {/* ═══════════════════════════════════════════ */}
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Megaphone className="w-4 h-4 text-primary" />
                      Indicação
                    </h3>

                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Programa */}
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="program"
                            className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5"
                          >
                            <Layers className="w-3 h-3" />
                            Programa *
                          </Label>
                          <Select
                            value={form.program_id}
                            onValueChange={(v) => update('program_id', v)}
                            disabled={activePrograms.length <= 1}
                          >
                            <SelectTrigger id="program" className="h-9 border-border/50">
                              <SelectValue placeholder="Selecione o programa" />
                            </SelectTrigger>
                            <SelectContent>
                              {activePrograms.map((p) => (
                                <SelectItem key={p.program_id} value={p.program_id}>
                                  {p.program_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.program_id && (
                            <p className="text-xs text-destructive">{errors.program_id}</p>
                          )}
                        </div>

                        {/* Campanha */}
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="campaign"
                            className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5"
                          >
                            <GitBranch className="w-3 h-3" />
                            Campanha *
                          </Label>
                          <Select
                            value={form.campaign_id}
                            onValueChange={(v) => update('campaign_id', v)}
                            disabled={!form.program_id || programCampaigns.length === 0}
                          >
                            <SelectTrigger id="campaign" className="h-9 border-border/50">
                              <SelectValue
                                placeholder={
                                  !form.program_id
                                    ? 'Selecione um programa primeiro'
                                    : programCampaigns.length === 0
                                      ? 'Nenhuma campanha disponível'
                                      : 'Selecione a campanha'
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {programCampaigns.map((c) => (
                                <SelectItem key={c.campaign_id} value={c.campaign_id}>
                                  {c.campaign_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.campaign_id && (
                            <p className="text-xs text-destructive">{errors.campaign_id}</p>
                          )}
                          {noCampaignsForProgram && !errors.campaign_id && (
                            <p className="text-xs text-muted-foreground">
                              Este programa ainda não tem campanhas ativas.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* ═══════════════════════════════════════════ */}
                  {/* Seção: Dados do Lead                        */}
                  {/* ═══════════════════════════════════════════ */}
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" />
                      Dados do Lead
                    </h3>

                    <div className="space-y-4">
                      {/* Nome + Telefone: sempre visíveis.
                          Quando o E-mail está oculto, Nome e Telefone ficam
                          lado a lado no desktop para o modal não ficar vazio. */}
                      <div
                        className={cn(
                          'grid gap-3',
                          showEmail ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2',
                        )}
                      >
                        {/* Nome do lead */}
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="lead_name"
                            className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5"
                          >
                            <User className="w-3 h-3" />
                            Nome do lead
                          </Label>
                          <Input
                            id="lead_name"
                            value={form.lead_name}
                            // Sanitização anti-XSS leve aplicada em tempo real.
                            onChange={(e) => update('lead_name', sanitizeText(e.target.value))}
                            placeholder="Ex: João da Silva"
                            maxLength={200}
                            className="h-9 border-border/50"
                          />
                          {errors.lead_name && (
                            <p className="text-xs text-destructive">{errors.lead_name}</p>
                          )}
                        </div>

                        {!showEmail && (
                          <div className="space-y-1.5">
                            <Label
                              htmlFor="lead_phone"
                              className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5"
                            >
                              <Phone className="w-3 h-3" />
                              WhatsApp / Telefone
                            </Label>
                            <PhoneInput
                              id="lead_phone"
                              value={form.lead_phone}
                              onChange={(v) => update('lead_phone', v)}
                              placeholder="(11) 99999-9999"
                              aria-invalid={!!errors.lead_phone}
                            />
                            {errors.lead_phone && (
                              <p className="text-xs text-destructive">{errors.lead_phone}</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Telefone + E-mail lado a lado (somente quando o
                          E-mail está habilitado na campanha). */}
                      {showEmail && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label
                              htmlFor="lead_phone"
                              className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5"
                            >
                              <Phone className="w-3 h-3" />
                              WhatsApp / Telefone
                            </Label>
                            {/* PhoneInput: DDI + máscara BR + suporte a 0800/4003. */}
                            <PhoneInput
                              id="lead_phone"
                              value={form.lead_phone}
                              onChange={(v) => update('lead_phone', v)}
                              placeholder="(11) 99999-9999"
                              aria-invalid={!!errors.lead_phone}
                            />
                            {errors.lead_phone && (
                              <p className="text-xs text-destructive">{errors.lead_phone}</p>
                            )}
                          </div>

                          <div className="space-y-1.5">
                            <Label
                              htmlFor="lead_email"
                              className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5"
                            >
                              <Mail className="w-3 h-3" />
                              E-mail
                            </Label>
                            <Input
                              id="lead_email"
                              type="email"
                              value={form.lead_email}
                              // Sanitização: remove < >, espaços e força lowercase.
                              onChange={(e) => update('lead_email', sanitizeEmail(e.target.value))}
                              placeholder="email@exemplo.com"
                              maxLength={200}
                              inputMode="email"
                              autoComplete="email"
                              className="h-9 border-border/50"
                            />
                            {errors.lead_email && (
                              <p className="text-xs text-destructive">{errors.lead_email}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Observação (configurável por campanha) */}
                      {showMessage && (
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="lead_message"
                            className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5"
                          >
                            <MessageSquare className="w-3 h-3" />
                            Observação (opcional)
                          </Label>
                          <Textarea
                            id="lead_message"
                            value={form.lead_message}
                            onChange={(e) => update('lead_message', sanitizeText(e.target.value))}
                            placeholder="Detalhes do interesse, anotações etc."
                            maxLength={2000}
                            rows={3}
                            className="border-border/50"
                          />
                          {errors.lead_message && (
                            <p className="text-xs text-destructive">{errors.lead_message}</p>
                          )}
                        </div>
                      )}

                      <p className="text-xs text-muted-foreground">
                        {showEmail
                          ? 'Informe ao menos nome, e-mail ou telefone.'
                          : 'Informe ao menos nome ou telefone.'}{' '}
                        Os dados serão enviados como uma nova indicação no
                        status "Pendente".
                      </p>
                    </div>

                  </div>
                </>
              )}
            </div>
          </ScrollArea>

          {/* Footer premium padronizado com TaskFormDialog. */}
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border/50 bg-muted/20 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
              className="text-muted-foreground hover:text-foreground"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={
                isPending ||
                noActivePrograms ||
                noCampaignsForProgram ||
                !form.campaign_id
              }
              className="min-w-[140px] gap-2"
            >
              {isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <UserPlus className="w-3.5 h-3.5" />
              )}
              {isPending ? 'Criando...' : 'Criar Indicação'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
